import { useCallback, useEffect, useRef, useState } from "react";

import OBR from "@owlbear-rodeo/sdk";
import { useAppSelector } from "../store/hooks";

const HEIGHT_EPSILON = 1; // px

/**
 * Mirrors the natural layout height of the panel into the Owlbear Rodeo iframe.
 */
export function useObrPanelHeight(debug = false) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const participantListRef = useRef<HTMLUListElement | null>(null);
  const lastRequestedHeight = useRef<number | null>(null);
  const lastViewportHeight = useRef<number>(0);
  const measurementFrame = useRef<number | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  // Subscribe to all Redux state changes
  const state = useAppSelector(state => state);

  const log = useCallback(
    (...args: unknown[]) => {
      if (debug) {
        console.debug("[Resize]", ...args);
      }
    },
    [debug]
  );

  const measureContentHeight = useCallback(() => {
    const containerEl = containerRef.current;
    if (!containerEl) {
      return null;
    }

    const participantEl = participantListRef.current;
    const participantClient = participantEl?.clientHeight ?? 0;
    const participantScroll = participantEl?.scrollHeight ?? participantClient;
    const participantOverflow = participantScroll - participantClient;
    const rawHeight =
      containerEl.scrollHeight - participantClient + participantScroll;
    const contentHeight = Math.ceil(rawHeight);

    log(
      `container scrollHeight=${containerEl.scrollHeight}, participant scrollHeight=${participantScroll}, participant clientHeight=${participantClient}, participant overflow=${participantOverflow}, measured content=${contentHeight}`
    );

    return contentHeight;
  }, [containerRef, participantListRef, log]);

  const scheduleContentMeasurement = useCallback(() => {
    const runMeasurement = () => {
      measurementFrame.current = null;

      const contentHeight = measureContentHeight();
      if (contentHeight == null) {
        return;
      }

      const previousHeight = lastRequestedHeight.current;
      if (
        previousHeight !== null &&
        Math.abs(contentHeight - previousHeight) <= HEIGHT_EPSILON
      ) {
        log(
          `Skip setHeight – delta ${Math.abs(
            contentHeight - previousHeight
          )} <= ${HEIGHT_EPSILON}`
        );
        return;
      }

      lastRequestedHeight.current = contentHeight;
      OBR.action
        .setHeight(contentHeight)
        .then(() => {
          log(`Requested OBR panel height ${contentHeight}`);
        })
        .catch((error) => {
          console.error("[Resize] Failed to set OBR panel height", error);
        });
    };

    if (measurementFrame.current !== null) {
      return;
    }

    if (typeof window !== "undefined") {
      if (typeof window.requestAnimationFrame === "function") {
        measurementFrame.current = window.requestAnimationFrame(runMeasurement);
        return;
      }

      measurementFrame.current = window.setTimeout(runMeasurement, 0);
      return;
    }

    runMeasurement();
  }, [measureContentHeight, log]);

  const applyViewportConstraint = useCallback(() => {
    const containerEl = containerRef.current;
    if (!containerEl) {
      return;
    }

    const participantEl = participantListRef.current;
    const viewportHeight = Math.round(window.innerHeight);

    if (participantEl) {
      // Only set max height when we have a scrollable list
      if (Math.abs(viewportHeight - lastViewportHeight.current) > HEIGHT_EPSILON) {
        setMaxHeight(viewportHeight);
        lastViewportHeight.current = viewportHeight;
        log(`Applied viewport constraint ${viewportHeight}`);
      }
    } else {
      // No list, remove height constraint
      setMaxHeight(undefined);
      if (lastViewportHeight.current !== 0) {
        lastViewportHeight.current = 0;
        log(`Removed viewport constraint (no participant list)`);
      }
    }

    scheduleContentMeasurement();
  }, [log, scheduleContentMeasurement]);

  // Trigger on Redux state changes
  useEffect(() => {
    applyViewportConstraint();
  }, [state, applyViewportConstraint]);

  // Trigger on window resize (separate effect, different lifecycle)
  useEffect(() => {
    const handleResize = () => applyViewportConstraint();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [applyViewportConstraint]);

  useEffect(() => {
    const observerTarget = containerRef.current;
    if (!observerTarget) {
      scheduleContentMeasurement();
      return;
    }

    let resizeObserver: ResizeObserver | undefined;
    let mutationObserver: MutationObserver | undefined;

    // Set up ResizeObserver for dimension changes
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        scheduleContentMeasurement();
      });
      resizeObserver.observe(observerTarget);
    }

    // Set up MutationObserver for content changes
    if (typeof MutationObserver !== "undefined") {
      mutationObserver = new MutationObserver(() => {
        scheduleContentMeasurement();
      });
      mutationObserver.observe(observerTarget, {
        childList: true,
        subtree: true
      });
    }

    scheduleContentMeasurement();

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [containerRef, scheduleContentMeasurement]);

  useEffect(() => {
    return () => {
      if (measurementFrame.current === null) {
        return;
      }

      if (typeof window !== "undefined") {
        if (typeof window.cancelAnimationFrame === "function") {
          window.cancelAnimationFrame(measurementFrame.current);
        } else {
          window.clearTimeout(measurementFrame.current);
        }
      }

      measurementFrame.current = null;
    };
  }, []);
  return { containerRef, participantListRef, maxHeight };
}
